import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";
import type { LeaveType, RequestStatus, Shift } from "./types";

export const SHIFT_LABELS: Record<Shift, string> = {
  SABAH: "Sabah",
  OGLE: "Öğle",
  GECE: "Gece",
  OFF: "OFF",
};

export const LEAVE_LABELS: Record<LeaveType, string> = {
  ANNUAL: "Yıllık izin",
  EXCUSE: "Mazeret izni",
  MARRIAGE: "Evlilik izni",
  BEREAVEMENT: "Cenaze izni",
  MEDICAL: "Rapor",
  TRAINING: "Eğitim",
  ASSIGNMENT: "Görev",
  FLIGHT: "Uçuş",
  MILITARY: "Askerlik",
  OTHER: "Diğer",
};

export const STATUS_LABELS: Record<RequestStatus, string> = {
  PENDING: "Bekliyor",
  APPROVED: "Onaylandı",
  REJECTED: "Reddedildi",
  CANCELLATION_PENDING: "İptal bekliyor",
  CHANGE_PENDING: "Değişiklik bekliyor",
  SUPERSEDED: "Değiştirildi",
  CANCELLED: "İptal edildi",
};

export function dateLabel(
  value: string,
  style: "short" | "long" | "month" = "long",
): string {
  try {
    if (style === "short")
      return format(parseISO(value), "d MMM", { locale: tr });
    if (style === "month")
      return format(parseISO(value), "LLLL yyyy", { locale: tr });
    return format(parseISO(value), "d MMMM yyyy EEEE", { locale: tr });
  } catch {
    return value;
  }
}

export function dateRangeLabel(start: string, end: string): string {
  return start === end
    ? dateLabel(start)
    : `${dateLabel(start, "short")} – ${dateLabel(end, "long")}`;
}

export function todayIso(): string {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

export function addIsoDays(date: string, amount: number): string {
  return format(addDays(parseISO(date), amount), "yyyy-MM-dd");
}

export function rangeDays(start: string, end: string): string[] {
  const count = Math.max(
    0,
    differenceInCalendarDays(parseISO(end), parseISO(start)),
  );
  return Array.from({ length: count + 1 }, (_, index) =>
    addIsoDays(start, index),
  );
}

export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? "")
    .join("")
    .toLocaleUpperCase("tr-TR");
}

export function numberLabel(value: number | null | undefined): string {
  return value === null || value === undefined
    ? "Bilinmiyor"
    : new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(
        value,
      );
}

export const SKILL_LABELS: Readonly<Record<string, string>> = {
  QMSTR: "QMSTR",
  L1: "L1",
  L2: "L2",
  B1: "B1",
  QMSTR_L1: "QMSTR L1",
  QMSTR_L2: "QMSTR L2",
  CQM: "CQM",
  TAP_TEST: "TAP TEST",
};

export const SKILL_OPTIONS = Object.keys(SKILL_LABELS);

export function skillLabel(code: string) {
  return SKILL_LABELS[code] ?? code;
}

export function normalizeStatus(value: string): RequestStatus {
  const key = value.toUpperCase().replaceAll("-", "_");
  const aliases: Record<string, RequestStatus> = {
    BEKLIYOR: "PENDING",
    PENDING: "PENDING",
    APPROVED: "APPROVED",
    ONAYLANDI: "APPROVED",
    REJECTED: "REJECTED",
    REDDEDILDI: "REJECTED",
    CANCELLATION_PENDING: "CANCELLATION_PENDING",
    CANCEL_PENDING: "CANCELLATION_PENDING",
    CHANGE_PENDING: "CHANGE_PENDING",
    SUPERSEDED: "SUPERSEDED",
    CANCELLED: "CANCELLED",
    CANCELED: "CANCELLED",
  };
  return aliases[key] ?? "PENDING";
}

export function roleLabel(role: string): string {
  if (role === "admin") return "Sistem yöneticisi";
  if (role === "chief") return "Başteknisyen";
  if (role === "supervisor") return "Şef";
  if (role === "manager") return "Müdür";
  return "Personel";
}

export function localDateTime(value: string): string {
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Europe/Istanbul",
    }).format(new Date(value));
  } catch {
    return value;
  }
}
